import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  FLOATING_BUTTONS_HIDDEN_CLASS,
  FLOATING_BUTTONS_TRANSITION
} from "@/components/support/useFloatingButtonsLift";
import { cn } from "@repo/design-system/utils";
import { MessageSquareText, Mic } from "lucide-react";
import { useState } from "react";

import { VoiceMessageDialog } from "./VoiceMessageDialog";
import { VoiceOrderDialog } from "./VoiceOrderDialog";

type ActiveDialog = "order" | "message" | null;

/**
 * The persistent voice-actions FAB, mounted once in {@link MainLayout} so it
 * floats over every authenticated page. Sits directly above the WhatsApp
 * support FAB (which owns the bottom-right corner) and shares its geometry so
 * the two read as a stack rather than colliding.
 *
 * Originally (KAN-143) this opened {@link VoiceOrderDialog} directly. KAN-192
 * turned it into a popover menu offering "Order by voice" (listed first/
 * primary, to keep the added-tap cost on the transactional flow as low as
 * possible) and "Leave a voice message" - each opens its own dialog. A
 * popover was chosen over folding this into `WhatsAppSupportButton` because
 * that FAB is a bare external link with no menu-owning behavior; turning it
 * into a menu would have changed an established "always opens WhatsApp
 * directly" affordance.
 */
export function VoiceOrderFab() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Voice actions"
            title="Voice actions"
            // Stacked directly above WhatsAppSupportButton (bottom-3 = 12px + h-11
            // = 44px + ~12px gap => bottom 68px), sharing its right offset
            // (right-2 / sm:right-6) and size so the two align. If that FAB's
            // geometry changes, update this to match. The checkout summary
            // column stops short of this stack's top edge: moving or resizing
            // either FAB (or the order assistant button stacked above this one,
            // at bottom-[7.75rem]) means updating
            // FLOATING_BUTTONS_STACK_HEIGHT_PX and
            // ASSISTANT_FAB_EXTRA_HEIGHT_PX and the mirrored corner constants (FAB_WIDTH_PX, FAB_RIGHT_PX,
            // FAB_RIGHT_SM_PX) in useFloatingButtonsLift. Like the
            // WhatsApp FAB it rides above a bottom action bar by
            // `--floating-buttons-lift` (see useFloatingButtonsLift).
            className={cn(
              "fixed bottom-[calc(4.25rem+var(--floating-buttons-lift,0px))] right-2 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg duration-300 hover:scale-110 hover:bg-purple-700 hover:shadow-xl sm:right-6",
              FLOATING_BUTTONS_TRANSITION,
              FLOATING_BUTTONS_HIDDEN_CLASS
            )}
          >
            <Mic className="h-5 w-5" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="w-64 p-1">
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setActiveDialog("order");
            }}
            className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-neutral-800"
          >
            <Mic
              className="h-4 w-4 text-purple-600 dark:text-purple-400"
              aria-hidden="true"
            />
            Order by voice
          </button>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setActiveDialog("message");
            }}
            className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-neutral-800"
          >
            <MessageSquareText
              className="h-4 w-4 text-purple-600 dark:text-purple-400"
              aria-hidden="true"
            />
            Leave a voice message
          </button>
        </PopoverContent>
      </Popover>
      <VoiceOrderDialog
        open={activeDialog === "order"}
        onOpenChange={(open) => setActiveDialog(open ? "order" : null)}
      />
      <VoiceMessageDialog
        open={activeDialog === "message"}
        onOpenChange={(open) => setActiveDialog(open ? "message" : null)}
      />
    </>
  );
}
